"use client";

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Trash2,
  Search,
  Volume2,
  ExternalLink,
  BookOpen,
  Calendar,
  ChevronDown,
  CheckSquare,
  X,
  Download as DownloadIcon,
  Play,
  Loader2,
  LibraryBig,
  Quote,
  RotateCw,
  Brain,
  Sparkles,
  ListChecks,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUser } from "@/lib/context/user-context";
import Link from "next/link";
import { useSavedWords } from "@/hooks/useSavedWords";
import { SpinnerIcon } from "@/components/ui/SpinnerIcon";
import { useI18n } from "@/lib/i18n";
import { dueBy } from "@/lib/spacedPlan";
import { CardStack } from "@/components/cardStack/CardStack";
import type { StackableItem } from "@/lib/types";
// import { MobileMenuButton } from "@/components/ui/mobile-menu-button";

interface SavedWord {
  word: string;
  translation: string;
  phonetic?: string;
  example?: string;
  source: 'youdao' | 'original';
  explains?: string[];
  savedAt: number;
  addedAt?: number;
  status?: 'NEW' | 'FUZZY' | 'MASTERED' | string;
  lastReviewedAt?: number | null;
  nextReviewAt?: number | null;
  stageIndex?: number;
  ease?: number;
  interval?: number;
  reps?: number;
  correctStreak?: number;
  wrongStreak?: number;
  bootstrapDone?: boolean;
  episodeId?: string;
  episodeTitle?: string;
  contextText?: string;
  episodeThumbnail?: string;
}

type ReviewStackItem = SavedWord & StackableItem & {
  id: string;
  cardHue: number;
  isPlaceholder?: boolean;
};

const EMPTY_REVIEW_CARDS: ReviewStackItem[] = Array.from({ length: 3 }, (_, idx) => ({
  id: `empty-review-card-${idx}`,
  word: '',
  translation: '',
  source: 'original',
  savedAt: 0,
  status: 'EMPTY',
  cardHue: 210 + idx * 12,
  isPlaceholder: true,
}));

const formatRelativeTime = (timestamp: number, translate: (key: string) => string, lang: string) => {
  const now = Date.now();
  const diff = timestamp - now;
  const absDiff = Math.abs(diff);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  const formatWithUnit = (value: number, unit: string) => {
    const rounded = Math.round(value);
    if (rounded === 0) return translate('savedWords.relative.now');
    const template = diff >= 0 ? translate('savedWords.relative.in') : translate('savedWords.relative.ago');
    return template.replace('{value}', String(rounded)).replace('{unit}', unit);
  };

  if (absDiff < minute) {
    return diff >= 0
      ? translate('savedWords.relative.dueSoon')
      : translate('savedWords.relative.justReviewed');
  }

  if (absDiff < hour) {
    return formatWithUnit(absDiff / minute, 'm');
  }

  if (absDiff < day) {
    return formatWithUnit(absDiff / hour, 'h');
  }

  if (absDiff < day * 30) {
    return formatWithUnit(absDiff / day, 'd');
  }

  return new Date(timestamp).toLocaleDateString(lang === 'chinese' ? 'zh-CN' : undefined);
};

const formatInterval = (interval?: number | null) => {
  if (interval === undefined || interval === null || interval <= 0) return null;

  if (interval > 1000) {
    const minutes = interval / (60 * 1000);
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = minutes / 60;
    if (hours < 48) return `${Math.round(hours)}h`;
    const days = hours / 24;
    if (days < 60) return `${Math.round(days)}d`;
    const months = days / 30;
    return `${Math.round(months)}mo`;
  }

  if (interval < 1) {
    return `${Math.round(interval * 24)}h`;
  }

  if (interval < 30) {
    return `${Math.round(interval)}d`;
  }

  return `${Math.round(interval / 30)}mo`;
};

const MetaChip = ({ label, value }: { label: string; value: string }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-muted/30 px-2 py-0.5 text-[8px] font-medium text-muted-foreground">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">{label}</span>
    <span className="text-foreground/90">{value}</span>
  </span>
);

const STATUS_BADGE_CLASSES: Record<string, string> = {
  NEW: 'border-sky-200 bg-sky-100 text-sky-700',
  FUZZY: 'border-amber-200 bg-amber-100 text-amber-700',
  MASTERED: 'border-emerald-200 bg-emerald-100 text-emerald-700',
};

const sourceStyles: Record<'youdao' | 'original', string> = {
  youdao: 'border-amber-200 bg-amber-50 text-amber-700',
  original: 'border-primary/30 bg-primary/5 text-primary',
};

export default function SavedWordsPage() {
  const { t, lang } = useI18n() as any;
  const { user, isLoading: userLoading } = useUser();
  const {
    savedWords: allSavedWords,
    loading,
    removeWord,
    fetchAllSavedWords,
    reviewWord,
  } = useSavedWords();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState<'all' | 'youdao' | 'original'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'alphabetical'>('newest');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMap, setSelectedMap] = useState<Record<string, boolean>>({});
  const PLAY_GAP_MS = 1000; // 每个单词之间的播放间隔（毫秒）
  const [isBatchPlaying, setIsBatchPlaying] = useState(false);
  const [reviewingWord, setReviewingWord] = useState<string | null>(null);
  const [blurAllExplains, setBlurAllExplains] = useState(false);
  const [revealedWords, setRevealedWords] = useState<Record<string, boolean>>({});

  const toggleWordReveal = (word: string) => {
    setRevealedWords((prev) => ({
      ...prev,
      [word]: !prev[word],
    }));
  };

  const ongoingSpeakAbortRef = useRef<AbortController | null>(null);

  const speakWord = useCallback(async (text: string) => {
    try {
      const { doSpeak } = await import('@/components/tts');

      if (ongoingSpeakAbortRef.current) {
        ongoingSpeakAbortRef.current.abort();
      }

      const controller = new AbortController();
      ongoingSpeakAbortRef.current = controller;

      try {
        await doSpeak({
          provider: 'EdgeTTS',
          text,
          lang: 'en',
          rate: 10,
          volume: 100,
          signal: controller.signal,
          onFinish: () => {
            if (ongoingSpeakAbortRef.current === controller) {
              ongoingSpeakAbortRef.current = null;
            }
          },
        });
      } finally {
        if (ongoingSpeakAbortRef.current === controller) {
          ongoingSpeakAbortRef.current = null;
        }
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        return;
      }
      console.error('Failed to speak word:', text, e);
      // 忽略发音失败
    }
  }, []);

  // const mobileMenuButton = (
  //   <MobileMenuButton ariaLabel={lang === 'chinese' ? '打开菜单' : 'Open menu'} />
  // );


  const translateRelativeTime = useCallback(
    (timestamp: number) => formatRelativeTime(timestamp, t, lang),
    [t, lang]
  );

  const statusLabels = useMemo<Record<string, string>>(
    () => ({
      NEW: t('savedWords.status.new'),
      FUZZY: t('savedWords.status.fuzzy'),
      MASTERED: t('savedWords.status.mastered'),
    }),
    [t]
  );

  const formatWithSuffix = useCallback(
    (value: number, suffix: string) => (lang === 'chinese' ? `${value}${suffix}` : `${value} ${suffix}`),
    [lang]
  );

  const formatDateLabel = useCallback((ts: number) => {
    const d = new Date(ts);
    const today = new Date();
    const yday = new Date();
    yday.setDate(today.getDate() - 1);
    const isSameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    if (isSameDay(d, today)) return t('savedWords.relative.today');
    if (isSameDay(d, yday)) return t('savedWords.relative.yesterday');
    return d.toLocaleDateString(lang === 'chinese' ? 'zh-CN' : undefined);
  }, [t, lang]);

  // 初始化加载
  useEffect(() => {
    if (user?.id) {
      fetchAllSavedWords();
    }
  }, [user?.id, fetchAllSavedWords]);

  // 过滤和排序单词
  const filteredWords = useMemo(() => {
    const list = [...(allSavedWords || [])];

    // 搜索过滤
    const bySearch = searchQuery
      ? list.filter((w) =>
          w.word.toLowerCase().includes(searchQuery.toLowerCase()) ||
          w.translation.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : list;

    // 来源过滤
    const bySource = selectedSource === 'all'
      ? bySearch
      : bySearch.filter((w) => w.source === selectedSource);

    // 排序
    const sorted = bySource.sort((a, b) => {
      if (sortBy === 'newest') return b.savedAt - a.savedAt;
      if (sortBy === 'oldest') return a.savedAt - b.savedAt;
      return a.word.localeCompare(b.word);
    });

    return sorted;
  }, [allSavedWords, searchQuery, selectedSource, sortBy]);

  const reviewCandidates = useMemo(() => {
    if (!allSavedWords || allSavedWords.length === 0) return [] as SavedWord[];
    try {
      return dueBy(allSavedWords as any, Date.now()) as SavedWord[];
    } catch (error) {
      console.error('Failed to compute review queue', error);
      return [] as SavedWord[];
    }
  }, [allSavedWords]);

  const reviewStackItems = useMemo<ReviewStackItem[]>(() => {
    return reviewCandidates.slice(0, 10).map((item, idx) => ({
      ...item,
      id: `${item.word}-${item.savedAt ?? idx}`,
      cardHue: (item.word.charCodeAt(0) + idx * 73) % 360,
    }));
  }, [reviewCandidates]);

  const hasReviewItems = reviewStackItems.length > 0;

  const [activeReviewWord, setActiveReviewWord] = useState<ReviewStackItem | null>(() =>
    reviewStackItems.length ? reviewStackItems[reviewStackItems.length - 1] : null
  );
  const [activeReviewSpeakKey, setActiveReviewSpeakKey] = useState(0);
  const lastActiveChangeRef = useRef<{ id: string; timestamp: number } | null>(null);

  const handleActiveReviewChange = useCallback((item: ReviewStackItem | null) => {
    if (!item || item.isPlaceholder) {
      lastActiveChangeRef.current = null;
      setActiveReviewWord((prev) => (prev === null ? prev : null));
      return;
    }

    setActiveReviewWord(item);

    const now = Date.now();
    const last = lastActiveChangeRef.current;
    const shouldSpeak = !last || last.id !== item.id || now - last.timestamp > 150;

    if (shouldSpeak) {
      lastActiveChangeRef.current = { id: item.id, timestamp: now };
      setActiveReviewSpeakKey((key) => key + 1);
    }
  }, []);

  useEffect(() => {
    if (!reviewStackItems.length) {
      handleActiveReviewChange(null);
      return;
    }
    handleActiveReviewChange(reviewStackItems[reviewStackItems.length - 1]);
  }, [handleActiveReviewChange, reviewStackItems]);

  useEffect(() => {
    if (!activeReviewWord || isBatchPlaying) return;
    void speakWord(activeReviewWord.word);
  }, [activeReviewWord, activeReviewSpeakKey, isBatchPlaying, speakWord]);

  const totalDueCount = reviewCandidates.length;
  const activeIntervalLabel = activeReviewWord?.interval != null
    ? formatInterval(activeReviewWord.interval)
    : null;
  const activeDueLabel = activeReviewWord?.nextReviewAt
    ? translateRelativeTime(activeReviewWord.nextReviewAt)
    : null;

  const handleReviewQuality = useCallback(
    async (word: SavedWord | null | undefined, quality: number) => {
      if (!word || reviewingWord) return;
      setReviewingWord(word.word);
      try {
        await reviewWord(word.word, quality, Date.now());
      } finally {
        setReviewingWord(null);
      }
    },
    [reviewWord, reviewingWord]
  );

  // 选择逻辑
  const selectedCount = useMemo(
    () => Object.values(selectedMap).filter(Boolean).length,
    [selectedMap]
  );

  const totalSavedCountLabel = formatWithSuffix((allSavedWords || []).length, t('savedWords.countSuffix'));
  const selectedCountLabel = selectedCount > 0 ? ` • ${formatWithSuffix(selectedCount, t('savedWords.selectedSuffix'))}` : '';

  const toggleSelectAll = useCallback(() => {
    if (!filteredWords.length) return;
    const allSelected = filteredWords.every((w) => selectedMap[w.word]);
    const next: Record<string, boolean> = {};
    filteredWords.forEach((w) => (next[w.word] = !allSelected));
    setSelectedMap(next);
  }, [filteredWords, selectedMap]);

  const clearSelection = useCallback(() => setSelectedMap({}), []);

  // 分组逻辑（按日期）
  const groupKeyForDate = (ts: number) => {
    const d = new Date(ts);
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`; // 粗略按日
  };

  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; items: SavedWord[] }>();
    filteredWords.forEach((w) => {
      const key = groupKeyForDate(w.savedAt);
      const ex = map.get(key);
      if (ex) ex.items.push(w);
      else map.set(key, { label: formatDateLabel(w.savedAt), items: [w] });
    });
    // 排序分组：根据每组中最新的时间排序（新到旧）
    return Array.from(map.entries()).sort((a, b) => {
      const an = Math.max(...a[1].items.map((i) => i.savedAt));
      const bn = Math.max(...b[1].items.map((i) => i.savedAt));
      return bn - an;
    });
  }, [filteredWords, formatDateLabel]);

  // 删除单词（单个）
  const deleteWord = async (word: string) => {
    await removeWord(word);
  };

  // 批量删除
  const deleteSelected = async () => {
    const targets = filteredWords.filter((w) => selectedMap[w.word]);
    for (const w of targets) {
      // 顺序删除，避免并发风暴
      // 忽略单个失败，整体继续
      try {
        // eslint-disable-next-line no-await-in-loop
        await removeWord(w.word);
      } catch {}
    }
    clearSelection();
  };

  // 导出
  const exportAsJSON = () => {
    const targets = selectedCount > 0 ? filteredWords.filter((w) => selectedMap[w.word]) : filteredWords;
    const blob = new Blob([JSON.stringify(targets, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saved-words-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAsCSV = () => {
    const targets = selectedCount > 0 ? filteredWords.filter((w) => selectedMap[w.word]) : filteredWords;
    const header = ['word', 'translation', 'phonetic', 'source', 'explains', 'example', 'savedAt', 'episodeId', 'episodeTitle'];
    const rows = targets.map((w) => [
      w.word,
      (w.translation || '').replace(/\n/g, ' '),
      w.phonetic || '',
      w.source,
      (w.explains || []).join(' | '),
      (w.example || '').replace(/\n/g, ' '),
      new Date(w.savedAt).toISOString(),
      w.episodeId || '',
      (w.episodeTitle || '').replace(/,/g, ' '),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saved-words-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 发音（批量）
  const playSelected = async () => {
    if (isBatchPlaying) return;
    setIsBatchPlaying(true);
    const targets = selectedCount > 0 ? filteredWords.filter((w) => selectedMap[w.word]) : filteredWords;
    try {
      for (const w of targets) {
        // eslint-disable-next-line no-await-in-loop
        await speakWord(w.word);
        // 轻微间隔
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, PLAY_GAP_MS));
      }
    } finally {
      setIsBatchPlaying(false);
    }
  };

  // Show loading while user context is still loading
  if (userLoading) {
    return (
      <>
        {/* {mobileMenuButton} */}
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center gap-3">
            <SpinnerIcon size={24} color="hsl(var(--muted-foreground))" />
            <p className="pl-2">{t('history.loading')}</p>
          </div>
        </div>
      </>
    );
  }

  // Only show login required after user context has loaded and user is null
  if (!user) {
    return (
      <>
        {/* {mobileMenuButton} */}
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="text-center py-8">
              <LibraryBig className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-lg font-semibold mb-2">{t('savedWords.loginRequiredTitle')}</h2>
              <p className="text-sm text-muted-foreground mb-4">{t('savedWords.loginRequiredDesc')}</p>
              <Button asChild>
                <Link href="/login">{t('common.signIn')}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Show loading while fetching saved words
  if (loading) {
    return (
      <>
        {/* {mobileMenuButton} */}
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center gap-3">
            <SpinnerIcon size={24} color="hsl(var(--muted-foreground))" />
            <p className="pl-2">{t('history.loading')}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* {mobileMenuButton} */}
      <div className="mx-auto px-4 py-4">
      <div
        className="relative sticky top-[0px] z-50 -mx-4 border-b border-border/70 px-4 pb-2 sm:pb-4 pt-4"
      >
        <div className="absolute inset-0 -z-10 bg-background" />
        <div className="flex flex-col gap-2 sm:gap-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <LibraryBig className="h-8 w-8" />
                <h1 className="text-2xl sm:text-3xl font-bold">
                  {t('savedWords.title')}
                </h1>
              </div>
              <p className={cn('text-muted-foreground', lang === 'chinese' && 'font-normal')}>
                {totalSavedCountLabel}
                {selectedCountLabel}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={blurAllExplains ? 'default' : 'outline'}
                size="sm"
                className={cn("h-8 sm:h-9 rounded-xl", lang === 'chinese' && 'font-normal')}
                onClick={() => {
                  setRevealedWords({});
                  setBlurAllExplains((prev) => !prev);
                }}
                aria-pressed={blurAllExplains}
              >
                <ListChecks className="mr-1 h-4 w-4" />
                <span className={lang === 'chinese' ? 'font-normal' : undefined}>
                  {blurAllExplains ? t('savedWords.blur.show') : t('savedWords.blur.hide')}
                </span>
              </Button>
              <div className="hidden items-center gap-2 sm:flex">
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("h-8 sm:h-9 rounded-xl", lang === 'chinese' && 'font-normal')}
                  onClick={playSelected}
                  disabled={isBatchPlaying}
                >
                  {isBatchPlaying ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      <span className={lang === 'chinese' ? 'font-normal' : undefined}>
                        {t('savedWords.play.playing')}
                      </span>
                    </>
                  ) : (
                    <>
                      <Play className="mr-1 h-4 w-4" />
                      <span className={lang === 'chinese' ? 'font-normal' : undefined}>
                        {selectedCount > 0 ? t('savedWords.play.playSelected') : t('savedWords.play.playAll')}
                      </span>
                    </>
                  )}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn("h-8 sm:h-9 rounded-xl", lang === 'chinese' && 'font-normal')}
                    >
                      <DownloadIcon className="mr-1 h-4 w-4" />
                      <span className={lang === 'chinese' ? 'font-normal' : undefined}>
                        {t('savedWords.export.label')}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem
                      className={cn("cursor-pointer text-xs", lang === 'chinese' && 'font-normal')}
                      onClick={exportAsJSON}
                    >
                      {t('savedWords.export.json')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className={cn("cursor-pointer text-xs", lang === 'chinese' && 'font-normal')}
                      onClick={exportAsCSV}
                    >
                      {t('savedWords.export.csv')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant={selectMode ? 'default' : 'outline'}
                  size="sm"
                  className={cn("h-8 sm:h-9 rounded-xl", lang === 'chinese' && 'font-normal')}
                  onClick={() => {
                    setSelectMode((v) => !v);
                    if (selectMode) setSelectedMap({});
                  }}
                >
                  <CheckSquare className="mr-1 h-4 w-4" />
                  <span className={lang === 'chinese' ? 'font-normal' : undefined}>
                    {selectMode ? t('savedWords.selectMode.exit') : t('savedWords.selectMode.enter')}
                  </span>
                </Button>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      "h-8 w-8 sm:h-9 sm:w-9 rounded-xl p-0 sm:hidden",
                      lang === 'chinese' && 'font-normal'
                    )}
                    aria-label={lang === 'chinese' ? '更多操作' : 'More actions'}
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    className={cn("cursor-pointer text-xs", lang === 'chinese' && 'font-normal')}
                    disabled={isBatchPlaying}
                    onClick={() => {
                      if (!isBatchPlaying) {
                        playSelected();
                      }
                    }}
                  >
                    {isBatchPlaying
                      ? t('savedWords.play.playing')
                      : selectedCount > 0
                        ? t('savedWords.play.playSelected')
                        : t('savedWords.play.playAll')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={cn("cursor-pointer text-xs", lang === 'chinese' && 'font-normal')}
                    onClick={exportAsJSON}
                  >
                    {t('savedWords.export.json')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={cn("cursor-pointer text-xs", lang === 'chinese' && 'font-normal')}
                    onClick={exportAsCSV}
                  >
                    {t('savedWords.export.csv')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={cn("cursor-pointer text-xs", lang === 'chinese' && 'font-normal')}
                    onClick={() => {
                      setSelectMode((v) => !v);
                      if (selectMode) setSelectedMap({});
                    }}
                  >
                    {selectMode ? t('savedWords.selectMode.exit') : t('savedWords.selectMode.enter')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="rounded-2xl ">
            <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
                <Input
                  placeholder={t('savedWords.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn(
                    "h-9 sm:h-10 rounded-xl border-border/60 bg-background pl-10",
                    lang === 'chinese' && 'font-normal placeholder:font-light'
                  )}
                />
              </div>

              <div className="flex w-full flex-wrap items-center gap-1.5 sm:w-auto sm:justify-end">
                <Button
                  variant={selectedSource === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className={cn("h-7 sm:h-9 rounded-xl px-3 text-sm", lang === 'chinese' && 'font-normal')}
                  onClick={() => setSelectedSource('all')}
                >
                  <span className={lang === 'chinese' ? 'font-normal' : undefined}>
                    {t('savedWords.filters.all')}
                  </span>
                </Button>
                {selectMode && (
                  <>
                    <div className="mx-1 hidden h-6 w-px bg-border sm:block" />
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn("h-7 sm:h-9 rounded-xl", lang === 'chinese' && 'font-normal')}
                      onClick={toggleSelectAll}
                    >
                      <span className={lang === 'chinese' ? 'font-normal' : undefined}>
                        {t('savedWords.selectMode.selectAll')}
                      </span>
                    </Button>
                    {selectedCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn("h-7 sm:h-9 rounded-xl", lang === 'chinese' && 'font-normal')}
                        onClick={clearSelection}
                      >
                        <X className="mr-1 h-3.5 w-3.5" />
                        <span className={lang === 'chinese' ? 'font-normal' : undefined}>
                          {t('savedWords.selectMode.clear')}
                        </span>
                      </Button>
                    )}
                  </>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="default"
                      className={cn("h-7 sm:h-9 gap-1 rounded-xl px-3 text-sm", lang === 'chinese' && 'font-normal')}
                    >
                      <span className={lang === 'chinese' ? 'font-normal' : undefined}>
                        {sortBy === 'newest' && t('savedWords.sort.newest')}
                        {sortBy === 'oldest' && t('savedWords.sort.oldest')}
                        {sortBy === 'alphabetical' && t('savedWords.sort.alphabetical')}
                      </span>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-36">
                    <DropdownMenuItem
                      onClick={() => setSortBy('newest')}
                      className={cn("cursor-pointer text-xs", lang === 'chinese' && 'font-normal')}
                    >
                      {t('savedWords.sort.newest')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setSortBy('oldest')}
                      className={cn("cursor-pointer text-xs", lang === 'chinese' && 'font-normal')}
                    >
                      {t('savedWords.sort.oldest')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setSortBy('alphabetical')}
                      className={cn("cursor-pointer text-xs", lang === 'chinese' && 'font-normal')}
                    >
                      {t('savedWords.sort.alphabetical')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </div>


      {(hasReviewItems || totalDueCount === 0) && (
        <Card className="mt-2 sm:mt-6 border-none shadow-none">
          <CardContent className="p-2">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,300px)_1fr] lg:items-center">
              <div className="flex justify-center lg:justify-start">
                <CardStack
                  items={hasReviewItems ? reviewStackItems : EMPTY_REVIEW_CARDS}
                  containerClassName="relative h-72 w-56 sm:h-64 sm:w-56"
                  cardClassName="absolute inset-0 cursor-grab"
                  stackConfig={{ rotation: 5, scale: 0.05, perspective: 800 }}
                  onActiveChange={handleActiveReviewChange}
                >
                  {(item) =>
                    item.isPlaceholder ? (
                      <div
                        className="flex h-full w-full flex-col items-center justify-center rounded-3xl text-muted-foreground shadow-md"
                        style={{
                          background: `linear-gradient(140deg, hsla(${item.cardHue}, 85%, 96%, 0.96), hsla(${item.cardHue}, 70%, 88%, 0.96))`,
                          borderColor: `hsla(${item.cardHue}, 55%, 55%, 0.35)`,
                        }}
                      >
                        <Sparkles className="mb-2 h-6 w-6 text-[#facc15]" />
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                          {t('savedWords.reviewAllCaughtUp')}
                        </span>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          "flex h-full w-full flex-col justify-between rounded-3xl p-5 shadow-xl",
                          blurAllExplains && "cursor-pointer"
                        )}
                        role={blurAllExplains ? "button" : undefined}
                        tabIndex={blurAllExplains ? 0 : undefined}
                        onClick={() => {
                          if (!blurAllExplains) return;
                          toggleWordReveal(item.word);
                        }}
                        onKeyDown={(event) => {
                          if (!blurAllExplains) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleWordReveal(item.word);
                          }
                        }}
                        style={{
                          background: `linear-gradient(140deg, hsla(${item.cardHue}, 85%, 92%, 0.95), hsla(${item.cardHue}, 70%, 80%, 0.95))`,
                          borderColor: `hsla(${item.cardHue}, 55%, 55%, 0.45)`,
                        }}
                      >
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="rounded-sm bg-primary/10 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-primary">
                              {statusLabels[item.status?.toUpperCase?.() || item.status || 'NEW'] || item.status || t('savedWords.status.new')}
                            </span>
                          <span className="text-xs text-muted-foreground">
                              {item.nextReviewAt ? translateRelativeTime(item.nextReviewAt) : t('savedWords.relative.now')}
                          </span>
                          </div>
                          <h3 className="text-3xl font-semibold text-foreground">
                            {item.word}
                          </h3>
                          {item.phonetic && (
                            <p className="text-sm font-mono text-muted-foreground">/{item.phonetic}/</p>
                          )}
                        </div>
                        <p
                          className={cn(
                            "line-clamp-4 text-sm text-muted-foreground transition filter",
                            blurAllExplains && !revealedWords[item.word] && "blur-sm select-none"
                          )}
                        >
                          {item.translation}
                        </p>
                      </div>
                    )
                  }
                </CardStack>
              </div>

              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-3 text-sm font-medium">
                  <span className={cn("inline-flex items-center gap-2 rounded-full px-3 py-1 border border-border/40", lang === 'chinese' && 'font-normal')}>
                    <Brain className="h-4 w-4" />
                    {t('savedWords.reviewNow')}
                  </span>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold border border-border/40">
                    {formatWithSuffix(totalDueCount, t('savedWords.due'))}
                  </span>
                  <span className="rounded-full bg-primary/5 px-2 py-0.5 text-xs border border-border/40">
                    {formatWithSuffix(hasReviewItems ? reviewStackItems.length : 0, t('savedWords.onDeck'))}
                  </span>
                </div>

                {hasReviewItems && activeReviewWord ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2 text-2xl font-semibold text-foreground sm:text-3xl">
                        <span>{activeReviewWord.word}</span>
                        {activeReviewWord.phonetic && (
                          <span className="text-sm font-mono text-muted-foreground">/{activeReviewWord.phonetic}/</span>
                        )}
                      </div>
                      <p
                        className={cn(
                          "text-sm text-muted-foreground transition filter",
                          blurAllExplains && activeReviewWord && !revealedWords[activeReviewWord.word] && "blur-sm select-none"
                        )}
                        onClick={() => {
                          if (!blurAllExplains || !activeReviewWord) return;
                          toggleWordReveal(activeReviewWord.word);
                        }}
                        role={blurAllExplains ? "button" : undefined}
                        tabIndex={blurAllExplains ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (!blurAllExplains || !activeReviewWord) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleWordReveal(activeReviewWord.word);
                          }
                        }}
                      >
                        {activeReviewWord.translation}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        {activeDueLabel && <MetaChip label={t('savedWords.next')} value={activeDueLabel} />}
                        {activeIntervalLabel && <MetaChip label={t('savedWords.interval')} value={activeIntervalLabel} />}
                        {activeReviewWord.correctStreak ? (
                          <MetaChip label={t('savedWords.correct')} value={`${activeReviewWord.correctStreak}`} />
                        ) : null}
                        {activeReviewWord.wrongStreak ? (
                          <MetaChip label={t('savedWords.wrong')} value={`${activeReviewWord.wrongStreak}`} />
                        ) : null}
                      </div>
                    </div>

                    {activeReviewWord.example && (
                      <blockquote
                        className={cn(
                          "rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4 text-sm italic text-primary/80 transition filter",
                          blurAllExplains && !revealedWords[activeReviewWord.word] && "blur-sm select-none"
                        )}
                        onClick={() => {
                          if (!blurAllExplains || !activeReviewWord) return;
                          toggleWordReveal(activeReviewWord.word);
                        }}
                        role={blurAllExplains ? "button" : undefined}
                        tabIndex={blurAllExplains ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (!blurAllExplains || !activeReviewWord) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleWordReveal(activeReviewWord.word);
                          }
                        }}
                      >
                        “{activeReviewWord.example}”
                      </blockquote>
                    )}

                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-9 min-w-[60px] rounded-xl px-4 text-sm shadow-sm"
                        onClick={() => handleReviewQuality(activeReviewWord, 1)}
                        disabled={reviewingWord === activeReviewWord.word}
                      >
                        {reviewingWord === activeReviewWord.word ? (
                          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCw className="mr-1 h-4 w-4" />
                        )}
                        {t('savedWords.reviewAgain')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 min-w-[6px] rounded-xl px-4 text-sm shadow-sm"
                        onClick={() => handleReviewQuality(activeReviewWord, 3)}
                        disabled={reviewingWord === activeReviewWord.word}
                      >
                        {t('savedWords.reviewHard')}
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        className="h-9 min-w-[60px] rounded-xl px-4 text-sm shadow-sm"
                        onClick={() => handleReviewQuality(activeReviewWord, 4)}
                        disabled={reviewingWord === activeReviewWord.word}
                      >
                        {t('savedWords.reviewGood')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-9 min-w-[60px] rounded-xl px-4 text-sm shadow-sm"
                        onClick={() => handleReviewQuality(activeReviewWord, 5)}
                        disabled={reviewingWord === activeReviewWord.word}
                      >
                        <Sparkles className="mr-1 h-4 w-4" />
                        {t('savedWords.reviewEasy')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-border/60 bg-muted/30 px-6 py-8 text-center text-muted-foreground lg:items-start lg:text-left">
                    <Sparkles className="h-8 w-8 text-[#fde047] animate-pulse" style={{ animation: 'wiggle 2s ease-in-out infinite' }} />
                    <style jsx>{`
                      @keyframes wiggle {
                        0%, 100% { transform: rotate(-5deg); }
                        50% { transform: rotate(5deg); }
                      }
                    `}</style>
                    <div className="space-y-1">
                      <p className="text-lg font-semibold text-foreground">
                        {t('savedWords.reviewAllCaughtUp')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('savedWords.reviewAllCaughtUpHint')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="mt-6 pb-6">
        {filteredWords.length === 0 ? (
          <Card className="rounded-3xl border border-dashed border-primary/30 bg-primary/5">
            <CardContent className="text-center py-12">
              <BookOpen className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="text-base font-semibold text-primary mb-2">{t('savedWords.empty.title')}</h3>
              <p className="text-sm text-primary/70">
                {searchQuery || selectedSource !== 'all'
                  ? t('savedWords.empty.filterHint')
                  : t('savedWords.empty.defaultHint')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {grouped.map(([key, group]) => (
              <section key={key} className="space-y-4">
                <div className="text-base font-semibold uppercase tracking-[0.2em] text-muted-foreground/70">
                  {group.label}
                </div>
                <div className="grid gap-4 sm:grid-cols-[repeat(auto-fill,minmax(265px,1fr))] xl:grid-cols-[repeat(auto-fill,minmax(265px,1fr))]">
                  {group.items.map((word) => {
                    const statusKey = word.status ? word.status.toUpperCase() : undefined;
                    const statusBadgeClass = statusKey ? STATUS_BADGE_CLASSES[statusKey] || 'border-border bg-muted text-muted-foreground' : null;
                    const statusLabel = statusKey ? statusLabels[statusKey] || statusKey : null;
                    const intervalLabel = formatInterval(word.interval);
                    const nextReviewLabel = word.nextReviewAt ? translateRelativeTime(word.nextReviewAt) : null;
                    const lastReviewLabel = word.lastReviewedAt ? translateRelativeTime(word.lastReviewedAt) : null;

                    const isBlurred = blurAllExplains && !revealedWords[word.word];

                    const handleCardToggle = () => {
                      if (!blurAllExplains) return;
                      toggleWordReveal(word.word);
                    };

                    return (
                      <div
                        key={`${word.word}-${word.savedAt}`}
                        className={cn(
                          'relative flex h-full max-w-[265px] flex-col gap-3 rounded-3xl border border-border/60 bg-background/95 p-3 shadow-sm ring-1 ring-transparent transition hover:shadow-lg hover:ring-primary/20',
                          blurAllExplains && 'cursor-pointer'
                        )}
                        role={blurAllExplains ? 'button' : undefined}
                        tabIndex={blurAllExplains ? 0 : undefined}
                        onClick={handleCardToggle}
                        onKeyDown={(event) => {
                          if (!blurAllExplains) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleCardToggle();
                          }
                        }}
                      >
                        {selectMode && (
                          <input
                            type="checkbox"
                            className="absolute right-4 top-4 h-4 w-4 accent-primary"
                            checked={!!selectedMap[word.word]}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(e) =>
                              setSelectedMap((prev) => ({ ...prev, [word.word]: e.target.checked }))
                            }
                          />
                        )}

                        <div className="space-y-2">
                          <div className="space-y-1.5 pr-8">
                            <h3 className="text-2xl font-semibold text-foreground">{word.word}</h3>
                            {word.phonetic && (
                              <p className="text-xs font-mono text-muted-foreground">/{word.phonetic}/</p>
                            )}
                            <p
                              className={cn(
                                'text-xs leading-relaxed text-muted-foreground transition filter',
                                isBlurred && 'blur-sm select-none'
                              )}
                            >
                              {word.translation}
                            </p>
                          </div>

                          <div className={cn("absolute right-3 top-3 flex items-center gap-1", selectMode && "top-10")}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground/70 hover:text-primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                speakWord(word.word);
                              }}
                            >
                              <Volume2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive/80 hover:text-destructive"
                              onClick={(event) => {
                                event.stopPropagation();
                                deleteWord(word.word);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <Badge
                              variant="outline"
                              className={cn(
                                'rounded-full border px-2.5 py-0.5 text-[8px] uppercase tracking-wide',
                                sourceStyles[word.source]
                              )}
                            >
                              {word.source === 'youdao' ? t('savedWords.sources.youdao') : t('savedWords.sources.original')}
                            </Badge>
                            {statusLabel && (
                              <Badge
                                variant="outline"
                                className={cn(
                                  'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[8px] font-semibold uppercase tracking-wide',
                                  statusBadgeClass
                                )}
                              >
                                {statusLabel}
                              </Badge>
                            )}
                            {nextReviewLabel && <MetaChip label={t('savedWords.next')} value={nextReviewLabel} />}
                            {lastReviewLabel && <MetaChip label={t('savedWords.reviewed')} value={lastReviewLabel} />}
                            {intervalLabel && <MetaChip label={t('savedWords.interval')} value={intervalLabel} />}
                          </div>

                          {word.explains && word.explains.length > 0 && (
                            <div className="space-y-1.5">
                              <p className="text-xs uppercase tracking-wide text-muted-foreground/60">
                                {t('savedWords.explains')}
                              </p>
                          <div className="flex flex-wrap gap-2">
                            {word.explains.slice(0, 4).map((explain, index) => (
                              <span
                                key={index}
                                className={cn(
                                  'rounded-full border border-border/50 bg-muted/30 px-3 py-1 text-[11px] text-muted-foreground transition filter',
                                  isBlurred && 'blur-sm select-none'
                                )}
                              >
                                {explain}
                              </span>
                            ))}
                            {word.explains.length > 4 && (
                              <span
                                className={cn(
                                  'rounded-full bg-muted/40 px-3 py-1 text-[11px] text-muted-foreground/80 transition filter',
                                  isBlurred && 'blur-sm select-none'
                                )}
                              >
                                +{word.explains.length - 4}
                              </span>
                            )}
                          </div>
                        </div>
                          )}

                          {word.example && (
                            <div className="rounded-2xl border border-dashed border-border/50 bg-muted/20 p-2.5">
                              <p className="text-xs italic text-muted-foreground/80 line-clamp-2">
                                “{word.example}”
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="mt-auto flex flex-wrap items-center gap-3 text-xs text-muted-foreground/80">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(word.savedAt).toLocaleDateString()}</span>
                          </div>
                          {word.episodeId && word.episodeTitle && (
                            <div className="flex items-center gap-1 min-w-0">
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                              <Link
                                href={`/episodes/${word.episodeId}`}
                                className="max-w-[220px] truncate text-primary hover:underline"
                                onClick={(event) => event.stopPropagation()}
                              >
                                {word.episodeTitle}
                              </Link>
                            </div>
                          )}
                          {word.contextText && (
                            <div className="flex min-w-0 items-center gap-1 text-muted-foreground/70">
                              <Quote className="h-3 w-3 flex-shrink-0" />
                              <span className="line-clamp-1 max-w-[220px]">{word.contextText}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* 底部批量操作条 */}
      {selectMode && selectedCount > 0 && (
        <div className="fixed bottom-[84px] left-1/2 -translate-x-1/2 z-20">
          <div className="rounded-2xl border bg-background/95 backdrop-blur px-3 py-2 shadow-lg flex items-center gap-2">
            <span className="text-sm text-muted-foreground px-2">{formatWithSuffix(selectedCount, t('savedWords.selectionBar.selectedSuffix'))}</span>
            <Button variant="destructive" size="sm" className="rounded-xl h-8" onClick={deleteSelected}>
              <Trash2 className="h-4 w-4 mr-1" /> {t('savedWords.selectionBar.delete')}
            </Button>
            <Button variant="outline" size="sm" className="rounded-xl h-8" onClick={exportAsJSON}>
              <DownloadIcon className="h-4 w-4 mr-1" /> {t('savedWords.export.jsonShort')}
            </Button>
            <Button variant="outline" size="sm" className="rounded-xl h-8" onClick={exportAsCSV}>
              <DownloadIcon className="h-4 w-4 mr-1" /> {t('savedWords.export.csvShort')}
            </Button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
